import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, inject, signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subject, catchError, distinctUntilChanged, finalize, forkJoin, map, of, switchMap, takeUntil } from 'rxjs';
import { OrderTimelineEvent } from '../../../core/services/order-timeline/order-timeline-event.model';
import { OrderTimelineService } from '../../../core/services/order-timeline/order-timeline.service';
import { OrdersService } from '../../../core/services/order/order.service';
import { WorkflowOrderService } from '../../../core/services/workflow-order/workflow-order.service';
import { WorkflowUserApproverModel } from '../../../core/models/workflow/workflow-order.model';
import { AuthenticationService } from '../../../core/services/authentication/authentication.service';
import { ModalComponent } from '../../../shared/components/modal/modal.component';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { ApprovalWorkflowComponent } from '../../../shared/components/approval-workflow/approval-workflow.component';
import {AlertComponent} from '../../../shared/components/alert/alert.component';
import { OrderInconsistenciesComponent } from './componennts/order-inconsistencies/order-inconsistencies.component';
import { OrderTimelineComponent } from './componennts/order-integration-timeline/order-timeline.component';

type Severity = 'error' | 'warning' | 'info';

type VtexIntegrationStep = {
  dataFimProc?: string;
  dataIniProc?: string;
  fila?: string;
  horaMsgAcao?: string;
  msgAcao?: string;
};

interface VtexDetailsExtra {
  cliente?: string;
  filial?: string;
  inconPagto?: string;
  inconTerraIn?: string;

  numNf?: string;
  serieNf?: string;
  tipoNf?: string;

  pedidoGan?: string;
  pedidoVtex?: string;
  tabela?: string;

  datasNf?: {
    dataEnv?: string;
    dataProc?: string;
    dataRet?: string;
    horaEnv?: string;
    horaProc?: string;
    horaRet?: string;
  };

  integracao?: {
    billing?: VtexIntegrationStep | null;
    billingCancel?: VtexIntegrationStep | null;
    order?: VtexIntegrationStep | null;
    statusVtex?: Array<{
      dataFimProc?: string;
      dataIniProc?: string;
      horaMsgAcao?: string;
      msgAcao?: string;
    }>;
  };
}

interface Inconsistency {
  severity: Severity;
  message: string;
  code?: string;
  invoice?: string;
}
interface VtexOrderTotalsLine {
  id: string;
  value: number;
  name?: string;
}

interface VtexOrderItem {
  productId?: string;
  name?: string;
  quantity: number;
  price?: number;
  sellingPrice?: number;
}

interface VtexOrderDetails {
  orderId: string;
  creationDate: string;
  status: string;

  salesChannel?: string;
  sellers?: Array<{ id?: string; name?: string }>;

  clientProfileData?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    document?: string;
  };

  shippingData?: {
    address?: any;
    selectedAddresses?: any[];
    logisticsInfo?: any[];
  };

  selectedAddresses?: any[];
  totals?: VtexOrderTotalsLine[];

  paymentData?: {
    transactions?: any[];
  };

  items?: VtexOrderItem[];

  ratesAndBenefitsData?: any;
}

@Component({
  selector: 'app-order',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalComponent, ButtonComponent, ApprovalWorkflowComponent, AlertComponent,OrderInconsistenciesComponent,OrderTimelineComponent],
  templateUrl: './order.component.html',
  styleUrl: './order.component.scss',
})
export class OrderComponent implements OnInit, OnDestroy {
  @ViewChild('reprocessModal') reprocessModal!: ModalComponent;
  callNumber = '';
  private destroy$ = new Subject<void>();
  loading = signal(false);
  sucessMsg = signal<string | null>(null);
  errorMsg = signal<string | null>(null);
  details = signal<VtexOrderDetails | null>(null);
  simulateDfdlOrder = signal<string | null>(null);
  simulateDfdlBilling = signal<string | null>(null);


  routeOrderId = signal<string>('');
  workflowData = signal<WorkflowUserApproverModel | null>(null);
  createWorkflowLoading = signal(false);

  showApprovalWorkflow = computed(() => this.workflowData() !== null);

  canPerformWorkflowActions = computed(() => {
    const w = this.workflowData();
    if (!w?.allowedRoles?.length) return true;
    return this.authService.hasAnyRole(w.allowedRoles);
  });

  customerName = computed(() => {
    const d = this.details();
    if (!d?.clientProfileData) return '-';
    const fn = d.clientProfileData.firstName ?? '';
    const ln = d.clientProfileData.lastName ?? '';
    const full = `${fn} ${ln}`.trim();
    return full || '-';
  });

  customerEmail = computed(() => this.details()?.clientProfileData?.email ?? '-');
  customerPhone = computed(() => this.details()?.clientProfileData?.phone ?? '-');
  customerDocument = computed(() => this.details()?.clientProfileData?.document ?? '-');

  detailsExtra = signal<VtexDetailsExtra | null>(null);
  orderTimeline = signal<{ occurredEvents: OrderTimelineEvent[], expectedEvents: OrderTimelineEvent[] } | null>(null);
  orderTimelineLoading = signal(false);
  isReprocessingOrder = computed(() => {
    const timeline = this.orderTimeline();
    if (!timeline) return false;

    const nextExpected = timeline.expectedEvents?.[0];

    if (!nextExpected) {
      return false;
    }

    const isKruzerToGan =
      nextExpected.fromSystemName === 'Kruzer' &&
      nextExpected.toSystemName === 'Gan';

    const isOrderOrBilling =
      nextExpected.description === 'dfdl billing' ||
      nextExpected.description === 'dfdl order' || nextExpected.description === "contrato prime criado"

    return isKruzerToGan && isOrderOrBilling;
  });
  invoiceText = computed(() => {
    const x = this.detailsExtra();
    if (!x) return null;

    const tipo = (x.tipoNf ?? '').trim();
    const filial = (x.filial ?? '').trim();
    const num = (x.numNf ?? '').trim();
    const serie = (x.serieNf ?? '').trim();
    const inconPagto = (x.inconPagto ?? '').trim();

    if (/^3P/i.test(inconPagto)) {
      return 'NF: emitida pelo parceiro';
    }

    const hasNumeroValido = !!num && num !== '0';
    const hasTipoOuSerie = !!tipo
    if (hasNumeroValido && (hasTipoOuSerie || !!filial)) {
      const numSerie = [num, serie].filter(Boolean).join('-');
      const parts = [tipo, filial, numSerie].filter(Boolean).join(' ');
      return `NF: ${parts}`.trim();
    }

    return 'NF: pendente emissão';
  });

  addressText = computed(() => {
    const d = this.details();
    const addr =
      d?.shippingData?.selectedAddresses?.[0] ??
      d?.selectedAddresses?.[0] ??
      d?.shippingData?.address;

    if (!addr) return '-';

    const street = addr.street ?? '';
    const number = addr.number ?? '';
    const complement = addr.complement ? `, ${addr.complement}` : '';
    const neighborhood = addr.neighborhood ? ` - ${addr.neighborhood}` : '';
    const city = addr.city ?? '';
    const state = addr.state ?? '';
    const postalCode = addr.postalCode ?? '';

    const line1 = `${street}, ${number}${complement}${neighborhood}`.replace(/\s+/g, ' ').trim();
    const line2 = `${city} - ${state}`.replace(/\s+/g, ' ').trim();
    const line3 = postalCode ? `${postalCode}` : '';

    return [line1, line2, line3].filter(Boolean).join('\n');
  });

  totalsVm = computed(() => {
    const totals = this.details()?.totals ?? [];
    const get = (id: string) => totals.find((t) => t.id === id)?.value ?? 0;

    const items = get('Items');
    const discounts = get('Discounts');
    const shipping = get('Shipping');
    const tax = get('Tax');

    const final = items + shipping + tax + discounts;

    return { items, discounts, shipping, tax, final };
  });

  inconsistencies = computed(() => this.validateDetails(this.details(), this.detailsExtra()));

  severitySummary = computed(() => {
    const list = this.inconsistencies();
    const hasError = list.some((x) => x.severity === 'error');
    const hasWarning = list.some((x) => x.severity === 'warning');
    if (hasError) return { label: 'Há inconsistências críticas', severity: 'error' as const };
    if (hasWarning) return { label: 'Há pontos de atenção', severity: 'warning' as const };
    return { label: 'Nenhuma inconsistência encontrada', severity: 'info' as const };
  });

  private readonly ordersService = inject(OrdersService);
  private readonly route = inject(ActivatedRoute);
  private readonly orderTimelineService = inject(OrderTimelineService);
  private readonly workflowOrderService = inject(WorkflowOrderService);
  private readonly authService = inject(AuthenticationService);

  ngOnInit(): void {
  this.route.paramMap
    .pipe(
      map((p) => (p.get('orderId') ?? '').trim()),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
      switchMap((orderId) => {
        this.routeOrderId.set(orderId);

        if (!orderId) {
          this.details.set(null);
          this.detailsExtra.set(null);
          this.errorMsg.set('orderId não informado na URL.');
          return of(null);
        }

        this.loading.set(true);
        this.errorMsg.set(null);
        this.details.set(null);
        this.detailsExtra.set(null);
        this.workflowData.set(null);
        this.orderTimeline.set(null);

        this.loadTimeline(orderId);

        return forkJoin({
          workflow: this.workflowOrderService.query(orderId).pipe(
            catchError((err) => {
              if (err?.status === 404) return of(null);
              return of(null);
            })
          ),
          vtex: this.ordersService.vtex(orderId).pipe(
            catchError((err) => {
              const msg =
                err?.error?.message ||
                err?.message ||
                'Não foi possível buscar o pedido (VTEX). Verifique o orderId e tente novamente.';
              this.errorMsg.set(msg);
              return of(null);
            })
          ),
          extra: this.ordersService.gan(orderId).pipe(
            catchError(() => of(null))
          ),
          dfdlOrder: this.ordersService.getDfdlOrder(orderId).pipe(
            catchError(() => of(null))
          ),
          dfdlBilling: this.ordersService.getDfdlBilling(orderId).pipe(
            catchError(() => of(null))
          ),
        }).pipe(finalize(() => this.loading.set(false)));
      })
    )
    .subscribe((res) => {
      if (!res) return;

      if (res.workflow) this.workflowData.set(res.workflow as WorkflowUserApproverModel);
      if (res.vtex) this.details.set(res.vtex as any);
      if (res.extra) this.detailsExtra.set(res.extra as any);
      if (res.dfdlOrder) {
        const parsedOrder = this.DFDLOrderToJson(res.dfdlOrder);
        this.simulateDfdlOrder.set(JSON.stringify(parsedOrder, null, 2));

        // this.simulateDfdlOrder.set(res.dfdlOrder);
      }

      if (res.dfdlBilling) {
        const parsedBilling = this.DFDLOrderToJson(res.dfdlBilling);
        this.simulateDfdlBilling.set(JSON.stringify(parsedBilling, null, 2));

        // this.simulateDfdlBilling.set(res.dfdlBilling);
      }


    });
}
  private loadTimeline(orderId: string): void {
    this.orderTimelineLoading.set(true);
    this.orderTimelineService
      .getTimeline(orderId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.orderTimelineLoading.set(false))
      )
      .subscribe({
        next: (timeline) => this.orderTimeline.set(timeline),
        error: () => this.orderTimeline.set(null),
      });
  }

  private prettifyJson(text: string): string {
    try {
      return JSON.stringify(JSON.parse(text), null, 2);
    } catch {
      return text;
    }
  }
  openReprocessModal(): void {
    if (this.workflowData()) return; // Workflow already exists
    this.reprocessModal.openDialog();
  }

  confirmReprocess(): void {
    if (this.workflowData()) return; // Must not create if workflow exists
    if (this.createWorkflowLoading()) return;

    const orderId = this.routeOrderId();
    if (!orderId) return;

    this.createWorkflowLoading.set(true);
    this.workflowOrderService
      .create(orderId)
      .pipe(finalize(() => this.createWorkflowLoading.set(false)))
      .subscribe({
        next: (workflow) => {
          this.reprocessModal.closeDialog();
          this.workflowData.set(workflow);
          this.sucessMsg.set('Workflow criado com sucesso.');
          this.errorMsg.set(null);
        },
        error: (err) => {
          const msg =
            err?.error?.message ||
            err?.message ||
            'Não foi possível criar o workflow. Tente novamente.';
          this.errorMsg.set(msg);
        },
      });
  }

  onWorkflowUpdated(workflow: WorkflowUserApproverModel | null): void {
    this.workflowData.set(workflow);
  }

  onWorkflowCancelled(): void {
    this.workflowData.set(null);
  }



  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onInputLimit(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.value.length > 12) {
      input.value = input.value.slice(0, 12);
    }
  }

  private validateDetails(details: VtexOrderDetails | null, extra: VtexDetailsExtra | null): Inconsistency[] {
    if (!details) return [];

    const list: Inconsistency[] = [];

    const nf = this.invoiceText();




    const push = (severity: Severity, message: string, code?: string, invoice?: string | null) =>
      list.push({ severity, message, code, invoice: invoice ? invoice : undefined });



    if (extra) {
      if (nf === 'NF: pendente emissão') {
        push('warning', 'Nota fiscal ainda não foi emitida.', 'INVOICE_PENDING', nf);
      } else if (nf) {
        push('info', 'Nota fiscal emitida.', 'INVOICE_EMITTED', nf);
      }
    }

    const inconPagto = (extra?.inconPagto ?? '').trim();

    if (inconPagto) {
      if (this.shouldIgnoreInconsistency(inconPagto)) {
        push('error', `Inconsistência de pagamento: ${inconPagto}`, 'INCON_PAGTO', nf);
      }
    }

    const inconTerraIn = (extra?.inconTerraIn ?? '').trim();
    if (inconTerraIn) {
      push('warning', `Inconsistência Terra/In: ${inconTerraIn}`, 'INCON_TERRAIN', nf);
    }

    // MOCK: Adicionando uma inconsistência de reprocessamento para fins de teste
    // if (!list.some(it => it.code === 'INVOICE_PENDING')) {
    //   push('warning', 'Nota fiscal ainda não foi emitida (MOCK).', 'INVOICE_PENDING', 'NF: pendente emissão');
    // }

    const weight: Record<Severity, number> = { error: 0, warning: 1, info: 2 };
    return list.sort((a, b) => weight[a.severity] - weight[b.severity]);
  }

  private shouldIgnoreInconsistency(text?: string | null): boolean {
    const s = (text ?? '').trim();

    if (!s) return true;

    if (/^NF:\s*\S+$/i.test(s)) {
      return true;
    }

    if (/^3P\b/i.test(s)) {
      return true;
    }

    return false;
  }
  formatCents(value: number): string {
    const brl = (value ?? 0) / 100;
    return brl.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  severityClass(sev: Severity): string {
    return sev === 'error' ? 'badge badge--error' : sev === 'warning' ? 'badge badge--warning' : 'badge badge--info';
  }

  severityDotClass(sev: Severity): string {
    return sev === 'error' ? 'dot dot--error' : sev === 'warning' ? 'dot dot--warning' : 'dot dot--info';
  }



  private  DFDLOrderToJson(payload:any, hideBlankValues = true) {
    const results:any = {};
    if (typeof payload !== 'string') return results;
    const lines = payload.split('\r');

    for (const line of lines) {
      const tagMatch = line.match(/^\[([A-Z]+)\]/);
      if (!tagMatch) continue;

      const tag = `[${tagMatch[1]}]`;
      const content = line.replace(/^\[[A-Z]+\]/, '');
      const values = content.split('|');

      const schema = this.schemas[tag];

      if (!schema) {
        results[tag] = results[tag] || [];
        results[tag].push({ raw: values });
        continue;
      }

      const mapped:any = {};
      for (let i = 0; i < schema.length; i++) {
        // @TODO TEMPORARIO
        // REMOVER APÓS DEBUG
        if (hideBlankValues) {
          if (values[i]) {
            mapped[schema[i]] = values[i] || 'BRANCO';
          }
        } else {
          mapped[schema[i]] = values[i] || 'BRANCO';
        }
      }

      results[tag] = results[tag] || [];
      results[tag].push(mapped);
    }

    return results;
  };

  private schemas:any = {
    '[CLI]': [
      'CustomerID',
      'CPF',
      'CNPJ',
      'OtherDoc',
      'DocumentType',
      'CustomerName',
      'Email',
      'BirthDay',
      'StreetName',
      'AdressName',
      'AdressNumber',
      'Complement',
      'StreetType',
      'District',
      'City',
      'ZipCode',
      'State',
      'Country',
      'HomePhone',
      'MobilePhone',
      'SendEmail',
      'SintegraSituationDate',
      'SintegraCodeSituation',
      'SuframaNumber',
      'SuframaSituation',
      'CNAENumber',
      'FederalRevenueStatus',
      'FederalRevenueStatusDate',
      'SimpleCompany',
      'FederalRevenueStatusDescription',
      'EconomicActiveDescription',
      'IBGECode',
      'SintegraSituation',
      'Gender',
      'Nome Social',
      '001',
      '002',
      '003',
      '004',
      '005',
      '006',
    ],
    '[ORD]': [
      'OrderID',
      'ClientOrderID',
      'SystemSource',
      'OrderDateTime',
      'OrderSubtotalAmount',
      'ShippingFreightValue',
      'OrderTotalShip',
      'InterestTotal',
      'DiscountTotal',
      'OrderTotalAmount',
      'OrderTotalItems',
      'ShippingMethod',
      'RecepientName',
      'QuantityMethodPayment',
      'DeliverAddressAddressName',
      'DeliverAddressStreetName',
      'DeliverAddressNumber',
      'DeliverAddressComplement',
      'DeliverAddressDistrict',
      'DeliverAddressCity',
      'DeliverAddressZipCode',
      'DeliverAddressState',
      'DeliverAddressCountry',
      'DeliverAddressStreetType',
      'DeliveryPhone',
      'DeliveryMobilePhone',
      'IsWeddingList',
      'WeddingListID',
      'CFNumber',
      'GiftCard',
      'ColorInformationPackage',
      'SmileID',
      'CommitteeReturn',
      'DeliveryRestrictionCode',
      'HotSiteID',
      'HotSiteDescription',
      'InventoryLocationID',
      'PricingTableId',
      'ScheduledOrderDate',
      'ScheduleDeliveryPeriod',
      'ScheduleDeliveryPeriodID',
      'ShippingEstimatedShippingDays',
      'TicketMaturityTerm',
      'VirtualPartnerID',
      'VirtualPartnerControl',
      'ShippingDate',
      'SystemCalcDate',
      'SessionId',
      'SellerId',
      'SellerName',
      'PricingTableInitialId',
      'OrderIDProvider',
      'DiscountAuthorizer',
      'DiscountReason',
      'IDParceiroMarketPlace',
      'NomeParceiroMarketPlace',
      'DaysType',
      'PeriodoDe',
      'PeriodoAte',
      'PayAgentRate',
      'IntegratingAgentRate',
      'OrderIDPack',
      'OrderIDOdd',
      'PrizeChannel',
      'isPlanSignBenefit',
      'me2token',
      'client_scr',
      'prioritized',
      'Observation',
      'ShipLabelIDPartner',
      'CashBack_ReservationId',
      'CashBack_Value',
      'SalesChannel',
      'PrimePlan',
      'OrderOrigin',
      'OrderEmail',
      'AffiliateId',
      'AccountName',
      'SellerOrderID',
      'MultipleShipments',
      'FutureSale',
    ],
    '[PG]': [
      'BillingAddressName',
      'BillingStreetName',
      'BillingStreetType',
      'BillingNumber',
      'BillingComplement',
      'BillingDistrict',
      'BillingCity',
      'BillingZipCode',
      'BillingState',
      'BillingCountry',
      'BillingMultipePayment',
    ],
    '[MP]': ['SendEmail'],
    '[PGL]': [
      'SequencePaymentID',
      'BillingMethodID',
      'BillingMethodPayValue',
      'FlagCardID',
      'InstallmentQuantity',
      'InstallmentValue',
      'BillingURLBoleto',
      'BillingLinhaDig',
      'BilletId',
      'paymentSystemVTEX',
      'Paymentsystem',
      'PaymentSystemName',
      'PaymentIdVTEX',
      'BillingBarCodeNumber',
      'PaymentGroupVTEX',
      'PaymentRedemptionCode',
    ],
    '[ITE]': [
      'SKU',
      'ProductID',
      'PrincipalProductID',
      'ItemName',
      'ItemCategory',
      'Quantity',
      'UnitPrice',
      'Interest',
      'Discount',
      'TotalPrice',
      'ProductBundle',
      'ServiceType',
      'ProductRelated',
      'MobiliyPlanCode',
      'MobilityPlanName',
      'MobileOperator',
      'PlanType',
      'Others',
      'isSubsidized',
      'MonthlyPayment',
      'isTriangulated',
      'PlanCharacteristic',
      'ProductM1',
      'ProductLine',
      'UnitPriceOpen',
      'CashBackValue',
      'UnitPriceWithDiscount',
      'ModalityType',
      'ProductReleaseDate',
      'ServiceLine',
      'ServiceCategory',
      'ServiceSubCategory',
      'IdSkuVTEX',
      'AccountNameSeller',
      'StockLocation',
      'DockIdVTEX',
      'BundleId',
      'skuProduto', // SkuProduto
      'marca', // Marca
      'modelo', // Modelo
      'lmi', // LMI
    ],
    '[STO]': [
      'RecordShopID',
      'PurchaseType',
      'ManagerID',
      'SellerID',
      'PriceTableID',
      'SubPriceTableID',
      'LCCode',
      'AffiliateInvoiceVF',
      'CPFNumber',
      'OtherDoc',
    ],
    '[NFL]': [
      'InvoiceVF',
      'SerieInvoiceVF',
      'SKUInvoice',
      'SKUQuantityReturned',
      'ValueItemsReturned',
      'AffiliateInvoiceVF',
      'GiverListCode',
      'LstLejour',
      'IDLejour',
    ],
    '[SHP]': [
      'Período',
      'Tipo de Entrega',
      'Tipo de correio',
      'Leadtime da entrega',
      'Data Entrega',
      'Código Transportadora GAN',
      'Custo Frete',
      'Dias de Transferência',
      'Filial estoque ressuprimento',
    ],
    '[OPEN]': ['OPEN_Type', 'OPEN_DataString'],
    '[H]': ['Program', 'Operation', 'User', 'Datetime', 'Version'],
    '[BIL]': [
      'ORDERID',
      'CLIENTORDERID',
      'SISTEMASOURCE',
      'ORDERDATETIME',
      'TOTALAMOUNT',
      'PLOTSNUMBER',
      'BILLINGSTATUS',
      'TNSUNUMBER',
      'AUTHORIZATIONNUMBER',
      'OPERATIONCARDCODE',
      'PAYMENTMETHODCODE',
      'PAYMENTMETHOD',
      'BUYERCODE',
      'PHONENUMBER',
      'CONTRACTNUMBER',
      'INDEX',
      'REASONCODE',
      'REASONDESCRIPTION',
      'PIXCODE',
      'AUTHORIZATIONDATE',
      'ESTABLISHMENT',
      'TERMINAL',
      'PRIORITIZED',
      'PRIORTYPE',
      'BINCARD',
      'PAYMENTGROUP',
      'PAYMENTSYSTEM',
      'PAYMENTSYSTEMNAME',
      'GIFTCARDID',
      'MethodID',
      'RedemptionCode',
      'SPLIT',
      'Period',
      'DeliveryType',
      'DeliveryCorrier',
      'LeadTime',
      'DeliveryDate',
      'DeliveryCarrier',
      'ShippingCost',
      'TransferredDaysResupply',
      'StoreIdResupply',
    ],
  };

}
